import type { LegalDocumentPastRecordsResponse, LegalDocumentPastSitesResponse } from '@safetyhub/shared';
import { api, type ApiResponse } from './api';

/**
 * 법정 서류 — **계약이 끝난 현장의 지난 기록**(L32 · 읽기 전용)의 API 클라이언트. 응답 계약의 정본은 shared
 * `features/legal-documents-past.ts`이고, 판정(경계 = 링크가 닫힌 시각)은 서버 `LegalDocumentPastAccessService`다.
 *
 * 형제 `lib/legalDocuments.ts`와 같은 규칙: 모든 호출이 `?companyId=` query를 싣는다(첫 인자 필수 — 빠지면 400).
 * ⚠️ 파일을 갈라 둔 이유 — 형제 모듈은 화면 spec이 `vi.mock`하는 대상이라 새 export가 그 spec들을
 * `No "X" export`로 죽인다.
 * ⛔ 쓰기 함수가 없다 — 서버에 쓰기 입구가 없다(사용자 결정: 읽기 전용).
 */

export const legalDocumentsPastQueryKeys = {
  sites: (companyId: string) => ['legal-documents', companyId, 'past-sites'] as const,
  records: (companyId: string, siteId: string) =>
    ['legal-documents', companyId, 'past-sites', siteId, 'records'] as const,
};

function unwrap<T>(body: ApiResponse<T>): T {
  if (!body.success || body.data === null) throw body;
  return body.data;
}

function pastSitePath(siteId: string): string {
  return `/legal-documents/past-sites/${encodeURIComponent(siteId)}`;
}

/** 지난 기록 현장 페이지 경로(화면 라우트) */
export function legalPastSitePath(siteId: string): string {
  return `/legal-documents/past-sites/${encodeURIComponent(siteId)}`;
}

export async function getLegalDocumentPastSites(companyId: string): Promise<LegalDocumentPastSitesResponse> {
  const { data } = await api.get<ApiResponse<LegalDocumentPastSitesResponse>>('/legal-documents/past-sites', {
    params: { companyId },
  });
  return unwrap(data);
}

export async function getLegalDocumentPastRecords(
  companyId: string,
  siteId: string
): Promise<LegalDocumentPastRecordsResponse> {
  const { data } = await api.get<ApiResponse<LegalDocumentPastRecordsResponse>>(
    `${pastSitePath(siteId)}/records`,
    { params: { companyId } }
  );
  return unwrap(data);
}

/** 지난 증빙 파일 — blob으로 받는다(형제 `downloadLegalObligationEvidenceFile`과 같은 이유: Bearer 전용) */
export async function downloadLegalDocumentPastEvidenceFile(
  companyId: string,
  siteId: string,
  evidenceId: string
): Promise<Blob> {
  const response = await api.get(`${pastSitePath(siteId)}/evidences/${encodeURIComponent(evidenceId)}/file`, {
    params: { companyId },
    responseType: 'blob',
  });
  return response.data as Blob;
}
